import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FileText, Car, Warehouse, Wrench, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    id: "claims-recovery",
    icon: FileText,
    title: "Accident claims recovery",
    body: [
      "If the accident wasn't your fault, we manage the whole claim against the at-fault party's insurer on your behalf: lodging the claim, dealing directly with the insurer, negotiating the settlement, and engaging legal representatives if needed.",
      "For not-at-fault drivers, we claim the costs of your claim, replacement hire vehicle, storage and repairs from the at-fault insurer – not from you.",
    ],
  },
  {
    id: "replacement-vehicles",
    icon: Car,
    title: "Replacement vehicles",
    body: [
      "While your car is off the road, we keep you moving with a replacement vehicle from our own rental fleet.",
      "Because the fleet is ours, there's no waiting on third parties – we arrange the vehicle as part of managing your claim.",
    ],
  },
  {
    id: "storage",
    icon: Warehouse,
    title: "Vehicle storage",
    body: [
      "Your damaged vehicle is stored securely at our own premises while your claim is underway.",
      "You don't need to organise towing or find somewhere to keep the car – we take care of it as part of the claim.",
    ],
  },
  {
    id: "repairs",
    icon: Wrench,
    title: "Repairs",
    body: [
      "We arrange assessment, quoting and repairs to pre-accident condition.",
      "We can arrange a repairer for you, or work with your chosen repairer – whichever you prefer.",
    ],
  },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="Claims recovery, replacement vehicles, storage & repairs | Reliance Motor Claims Recovery"
        description="We manage your not-at-fault claim, provide a replacement vehicle from our own fleet, store your damaged vehicle securely and arrange repairs to pre-accident condition."
        path="/services"
      />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-steel border-l-4 border-road pl-3">
            Our services
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-ink max-w-3xl">
            One call. Everything taken care of.
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-steel max-w-2xl">
            From the first phone call to the final repair, we manage the whole process for
            not-at-fault drivers – and recover the costs from the at-fault party's insurer,
            not from you.
          </p>
        </div>
      </section>

      {SECTIONS.map((section, i) => (
        <section
          key={section.id}
          id={section.id}
          className={`${i % 2 === 0 ? "bg-mist" : "bg-white"} scroll-mt-24`}
          data-testid={`services-section-${section.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-3">
                <div className="inline-flex items-center justify-center size-16 sm:size-20 bg-white rounded-xl border-l-4 border-road shadow-sm">
                  <section.icon className="size-8 sm:size-10 text-ink" strokeWidth={1.75} aria-hidden="true" />
                </div>
              </div>
              <div className="lg:col-span-9">
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink">
                  {section.title}
                </h2>
                {section.body.map((p) => (
                  <p key={p} className="mt-4 text-base sm:text-lg leading-relaxed text-steel max-w-3xl">
                    {p}
                  </p>
                ))}
                <a
                  href="/claim"
                  className="mt-7 inline-flex items-center justify-center gap-2 min-h-[48px] px-7 bg-ink text-white font-saira font-bold rounded hover:bg-black transition-colors"
                  data-testid={`services-start-claim-${section.id}`}
                >
                  Start your claim
                  <ArrowRight className="size-5" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </section>
      ))}
    </>
  );
}
